import React, {useCallback, useMemo} from 'react';
import {type DropdownProps} from 'semantic-ui-react';

import {getPanelSpec} from '../../util/panelTypes';
import {useParsedReportSpec} from '../../util/report/useParsedReportSpec';
import {isPanelGrid} from '../Slate/plugins/panel-grid';
import * as S from './ReportSectionPicker.styles';
import {type DropdownOption} from './types';

const NEW_SECTION_LABEL = 'New panel grid';
export const NEW_SECTION_IDX = -1;
const NEW_SECTION_OPTION: DropdownOption = {
  text: NEW_SECTION_LABEL,
  value: NEW_SECTION_IDX,
};

interface ReportSectionPickerProps {
  reportID: string;
  selectedSectionIdx: number;
  className?: string;
  onSelectSection(i: number): void;
}

const ReportSectionPicker: React.FC<ReportSectionPickerProps> = React.memo(
  ({reportID, selectedSectionIdx, className, onSelectSection}) => {
    const reportConfig = useParsedReportSpec(reportID);

    const opts: DropdownOption[] = useMemo(() => {
      if (!reportConfig) {
        return [NEW_SECTION_OPTION];
      }

      const sectionPanelTitles = reportConfig.blocks
        .filter(isPanelGrid)
        .map(pg => {
          const {metadata} = pg;
          return metadata.panelBankSectionConfig.panels
            .map(p => {
              const panelSpec = getPanelSpec(p.viewType);
              return (
                panelSpec.getTitleFromConfig?.(p.config as any) ||
                panelSpec.type
              );
            })
            .join(', ');
        });

      const choices = sectionPanelTitles.map((t, i) => {
        let text = `Panel grid ${i + 1}`;
        if (t !== '') {
          text += ` (${t})`;
        }
        return {
          text,
          value: i,
        };
      });

      return [NEW_SECTION_OPTION, ...choices];
    }, [reportConfig]);

    const onChange = useCallback(
      (e: React.SyntheticEvent, {value}: DropdownProps) => {
        // TODO: is this actually a number? pre-existing potential bug
        onSelectSection(value as number);
      },
      [onSelectSection]
    );

    return (
      <S.Dropdown
        selection
        className={className}
        options={opts}
        value={selectedSectionIdx}
        onChange={onChange}
      />
    );
  }
);

export default ReportSectionPicker;
